/**
 * Diagnostics for the browser-side add-to-cart chain (see
 * docs/observability/d2c-add-to-cart-flow-logging.md).
 *
 * A flow buffers the events of one click in memory and writes them in order
 * when the click ends, so a failure keeps its full context. A confirmed add is
 * logged too, with every event at level info; a failure keeps its warn/error
 * levels and additionally sends one RUM error.
 *
 * Telemetry must never affect the storefront: every public function swallows
 * its own errors. Log fields come from an allowlist; free-form values are
 * sanitized in ./anonymize.
 */

import {
  fingerprint,
  safePath,
  sanitizeHeaderValue,
  sanitizeText,
} from "./anonymize";
import { MESSAGE_PREFIX, readableMessage } from "./cart-flow-messages";
import { browserLogger, isDatadogInitialized } from "./datadog-browser";

export type CartFlowPhase =
  | "validation"
  | "cart_context_get"
  | "context_parse"
  | "post"
  | "response_validation"
  | "ui";

export type CartFlowReason =
  | "ok"
  | "invalid_request"
  | "context_network"
  | "context_timeout"
  | "context_invalid_response"
  | "context_http_error"
  | "context_no_csrf"
  | "context_flag_ambiguous"
  | "post_network"
  | "post_timeout"
  | "post_http_error"
  | "post_bad_payload"
  | "post_accelerator_error"
  | "unexpected_error";

export type CartFlowOutcome = "success" | "unknown" | "error";

type FieldValue = string | number | boolean | null | undefined;
export type CartFlowFields = Record<string, FieldValue>;
type LogLevel = "info" | "warn" | "error";

interface StepOptions {
  level?: LogLevel;
  /** Raw secrets, logged only as a salted fingerprint under the given field. */
  secrets?: Record<string, string | null | undefined>;
}

export interface CartFlowContext {
  country: string;
  language: string;
  productCode: string;
  quantity: number;
  stockStatus?: string;
}

export interface CartFlowResult {
  outcome: CartFlowOutcome;
  reason: CartFlowReason;
  phase: CartFlowPhase;
  postAttempted: boolean;
  /** True when the accelerator confirmed the add or rejected it explicitly. */
  outcomeKnown: boolean;
  errorName?: string;
  errorMessage?: string;
  /** The caught exception, if any. Kept as the `cause` of the RUM error. */
  error?: unknown;
}

export interface CartFlow {
  readonly id: string;
  step(event: string, fields?: CartFlowFields, options?: StepOptions): void;
  finish(result: CartFlowResult): void;
}

interface BufferedStep {
  event: string;
  level: LogLevel;
  fields: CartFlowFields;
  secrets?: StepOptions["secrets"];
  elapsedMs: number;
  pageAgeMs: number;
}

type RequestKind = "cart_context" | "cart_add" | "cart_count";

const OPERATION = "add_to_cart";
const EVENT_PREFIX = OPERATION;
const CART_COUNT_EVENT = "cart_count.fetch";
const MAX_CART_COUNT_FAILURE_LOGS = 10;
const HTTP_STATUS_MIN = 100;
const HTTP_STATUS_MAX = 599;
const HTTP_STATUS_CLASS_DIVISOR = 100;

const CORRELATION_HEADERS = [
  "x-request-id",
  "x-vercel-id",
  "x-datadog-trace-id",
  "cf-ray",
  "x-amzn-trace-id",
] as const;
const DIAGNOSTIC_HEADERS = ["server", "via", "x-cache", "retry-after"] as const;

const CONTACT_STORAGE_KEY = "scott:accelerator-contact";
/** A noted trigger belongs to the refresh that starts right after it. */
const CART_COUNT_TRIGGER_MAX_AGE_MS = 100;

let clickCount = 0;
let activeFlow: { id: string; startedAt: number } | null = null;
let requestCounter = 0;
let cartCountFailureLogs = 0;
let lastCartCountFailure: { at: number; status?: number } | null = null;
let acceleratorResponses = 0;
let cartCountFailuresInARow = 0;
let cartCountSuccesses = 0;
let lastCartCountSuccessAt: number | null = null;
let lastAttempt: { outcome: string; errorCategory: string; at: number } | null =
  null;
let pendingCartCountTrigger: { name: string; at: number } | null = null;
const inFlightRequests = new Map<number, RequestKind>();
const pendingWrites = new Set<Promise<unknown>>();

/**
 * Whether this browser tab already talked to the accelerator before a request
 * was sent. The accelerator opens its session with the first answer, whatever
 * its status (a 901 sets the session cookie too), so `none` marks a request
 * that was sent without a session of this tab. The session cookie itself is
 * HttpOnly and cannot be read here.
 *
 * - `none`: no answer seen yet in this tab
 * - `thisPage`: an answer arrived earlier on this page
 * - `earlierInTab`: only on an earlier page of this tab (before a reload)
 */
function acceleratorContactBefore(): "none" | "thisPage" | "earlierInTab" {
  if (acceleratorResponses > 0) {
    return "thisPage";
  }

  try {
    return sessionStorage.getItem(CONTACT_STORAGE_KEY)
      ? "earlierInTab"
      : "none";
  } catch {
    return "none";
  }
}

function markAcceleratorContact(): void {
  acceleratorResponses += 1;

  try {
    sessionStorage.setItem(CONTACT_STORAGE_KEY, "1");
  } catch {
    // Storage may be unavailable; the in-memory count still works.
  }
}

/**
 * Length of `document.cookie`, a number only. It covers just the cookies that
 * scripts can read (HttpOnly ones such as the accelerator session are not
 * included), so it is a hint for the size of the request headers, not their
 * size.
 */
function documentCookieLength(): number | undefined {
  try {
    return typeof document === "undefined" ? undefined : document.cookie.length;
  } catch {
    return;
  }
}

type NetworkInformation = {
  effectiveType?: string;
  rtt?: number;
  downlink?: number;
  saveData?: boolean;
};

/**
 * The browser's own view of its connection and the page the request came
 * from: is it online, how good does it think the network is (Chromium only),
 * and the pathname (never a query) of the page. Nothing here identifies a
 * person.
 */
function environmentFields(): CartFlowFields {
  try {
    const connection = (
      navigator as Navigator & { connection?: NetworkInformation }
    ).connection;

    return {
      online: navigator.onLine,
      connEffectiveType: connection?.effectiveType,
      connRttMs: connection?.rtt,
      connDownlinkMbps: connection?.downlink,
      connSaveData: connection?.saveData,
      pagePath: safePath(window.location.href) ?? undefined,
    };
  } catch {
    return {};
  }
}

/** What was known about the browser and its session when a request was sent. */
function requestSessionFields(): CartFlowFields {
  return {
    acceleratorContactBefore: acceleratorContactBefore(),
    documentCookieLength: documentCookieLength(),
    ...environmentFields(),
  };
}

function now(): number {
  return typeof performance === "undefined" ? Date.now() : performance.now();
}

function createId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now()}-${requestCounter}`;
  }
}

function visibilityState(): string | undefined {
  return typeof document === "undefined" ? undefined : document.visibilityState;
}

function definedFields(fields: CartFlowFields): Record<string, FieldValue> {
  return Object.fromEntries(
    Object.entries(fields).filter(([, value]) => value !== undefined)
  );
}

function writeLog(level: LogLevel, event: string, fields: CartFlowFields) {
  const context = { event, ...definedFields(fields) };
  // The message is what a person reads in the log list; `event` and the
  // fields stay the machine-readable part.
  const message = readableMessage(event, fields);

  if (level === "error") {
    browserLogger.error(message, undefined, context);
  } else if (level === "warn") {
    browserLogger.warn(message, context);
  } else {
    browserLogger.info(message, context);
  }
}

// --- Response and error descriptions ---------------------------------------

function statusClass(status: number | undefined): string {
  if (typeof status !== "number") {
    return "unknown";
  }

  if (status < HTTP_STATUS_MIN || status > HTTP_STATUS_MAX) {
    return "non_standard";
  }

  return `${Math.floor(status / HTTP_STATUS_CLASS_DIVISOR)}xx`;
}

function mediaType(contentType: string | null | undefined): string | undefined {
  return contentType?.split(";", 1)[0]?.trim().toLowerCase() || undefined;
}

function upperFirst(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

/** `x-request-id` → `headerXRequestId`. */
function headerFieldName(header: string): string {
  return `header${header.split("-").map(upperFirst).join("")}`;
}

/** `add_to_cart.context_fetch` → `contextFetch`. */
function stepKey(event: string): string {
  const [first = "", ...rest] = event.slice(event.indexOf(".") + 1).split("_");

  return first + rest.map(upperFirst).join("");
}

/**
 * Sanitized facts about a response: status, redirect target path, media type
 * and an allowlist of edge/proxy headers. Never includes cookies or bodies.
 */
export function describeResponse(response: Response): CartFlowFields {
  const status =
    typeof response.status === "number" ? response.status : undefined;
  const fields: CartFlowFields = {
    httpStatus: status,
    statusClass: statusClass(status),
    redirected: Boolean(response.redirected),
    finalPath: safePath(response.url),
    contentType: mediaType(response.headers?.get("content-type")),
    // basic / cors / opaque / opaqueredirect: how fetch classified the answer.
    responseType: typeof response.type === "string" ? response.type : undefined,
  };

  for (const header of [...CORRELATION_HEADERS, ...DIAGNOSTIC_HEADERS]) {
    const value = sanitizeHeaderValue(response.headers?.get(header));

    if (value) {
      fields[headerFieldName(header)] = value;
    }
  }

  fields.correlationId = CORRELATION_HEADERS.map(
    (header) => fields[headerFieldName(header)]
  ).find((value) => typeof value === "string");

  return fields;
}

/** Size and kind of a response body, without any of its content. */
export function describeBody(response: Response, text: string): CartFlowFields {
  const contentType = mediaType(response.headers?.get("content-type"));
  let kind = "other";

  if (text.length === 0) {
    kind = "empty";
  } else if (contentType?.includes("json")) {
    kind = "json";
  } else if (
    contentType?.includes("html") ||
    text.trimStart().startsWith("<")
  ) {
    kind = "html";
  }

  return { bodyLength: text.length, bodyKind: kind };
}

/** Reads a body for diagnostics only; a failing read must not change the flow. */
export async function readBodyText(response: Response): Promise<string> {
  try {
    return await response.text();
  } catch {
    return "";
  }
}

type ResourceTimingEntry = PerformanceResourceTiming & {
  /** Status the browser saw; not available in every browser. */
  responseStatus?: number;
};

/** The entry of a request may start a moment before our own timestamp. */
const RESOURCE_TIMING_START_TOLERANCE_MS = 5;

function wholeMs(value: number): number {
  return Math.max(0, Math.round(value));
}

/**
 * What the browser itself measured for one request (Resource Timing): where
 * the time went (redirect, DNS, connection, TLS, waiting for the first byte,
 * download), the protocol, whether a connection was reused and the status the
 * browser saw. Numbers and the protocol name only, never a URL.
 *
 * The request is found by its URL and a start time not before `startedAt`. The
 * entry exists only once the request has finished, so call this after the body
 * was read. Without an entry (buffer full, unsupported browser) the result is
 * empty.
 *
 * @param path the requested path or URL
 * @param startedAt `performance.now()` taken just before the request was sent
 */
export function resourceTimingFields(
  path: string,
  startedAt: number
): CartFlowFields {
  try {
    if (
      typeof performance === "undefined" ||
      typeof performance.getEntriesByType !== "function"
    ) {
      return {};
    }

    const url = new URL(path, window.location.href).href;
    const [entry] = (
      performance.getEntriesByType("resource") as ResourceTimingEntry[]
    )
      .filter(
        (candidate) =>
          candidate.name === url &&
          candidate.startTime >= startedAt - RESOURCE_TIMING_START_TOLERANCE_MS
      )
      .sort((first, second) => first.startTime - second.startTime);

    if (!entry) {
      return {};
    }

    // Detailed timestamps are zero when the browser withholds them (e.g. for a
    // cross-origin request without Timing-Allow-Origin); then only the total
    // duration and sizes are meaningful.
    const detailed = entry.requestStart > 0;

    return definedFields({
      netProtocol: entry.nextHopProtocol || undefined,
      netStatus:
        typeof entry.responseStatus === "number"
          ? entry.responseStatus
          : undefined,
      netTotalMs: wholeMs(entry.duration),
      netRedirectMs: detailed
        ? wholeMs(entry.redirectEnd - entry.redirectStart)
        : undefined,
      netDnsMs: detailed
        ? wholeMs(entry.domainLookupEnd - entry.domainLookupStart)
        : undefined,
      netConnectMs: detailed
        ? wholeMs(entry.connectEnd - entry.connectStart)
        : undefined,
      netTlsMs:
        detailed && entry.secureConnectionStart > 0
          ? wholeMs(entry.connectEnd - entry.secureConnectionStart)
          : undefined,
      netWaitMs: detailed
        ? wholeMs(entry.responseStart - entry.requestStart)
        : undefined,
      netDownloadMs: detailed
        ? wholeMs(entry.responseEnd - entry.responseStart)
        : undefined,
      netConnectionReused: detailed
        ? entry.connectEnd === entry.connectStart
        : undefined,
      netTransferBytes: entry.transferSize,
      netBodyBytes: entry.encodedBodySize,
    });
  } catch {
    return {};
  }
}

export function describeError(error: unknown): CartFlowFields {
  // DOMException (e.g. AbortError) is not an Error subclass in every runtime,
  // so read the name from any object that carries one.
  const name =
    typeof error === "object" &&
    error !== null &&
    "name" in error &&
    typeof error.name === "string"
      ? error.name
      : typeof error;
  let category = "other";

  if (name === "AbortError" || name === "TimeoutError") {
    category = "timeout";
  } else if (name === "TypeError") {
    // fetch rejects with TypeError for network failures and for responses the
    // browser refuses to expose, e.g. a non-standard status code.
    category = "network";
  }

  // The browser's own wording ("Failed to fetch", "Load failed", "NetworkError
  // when attempting to fetch resource.") tells browsers and causes apart.
  const message =
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof error.message === "string"
      ? sanitizeText(error.message)
      : "";

  return {
    errorName: name,
    errorKind: category,
    errorText: message || undefined,
    noResponse: true,
  };
}

// --- In-flight cart requests -----------------------------------------------

/**
 * Registers a cart request so overlapping requests (the cart page GET and the
 * header's mini-cart GET) show up in each other's diagnostics.
 */
export function beginCartRequest(kind: RequestKind) {
  requestCounter += 1;
  const key = requestCounter;
  inFlightRequests.set(key, kind);
  // Captured when the request is sent: a parallel request that is still in
  // flight has not been answered yet and must not count as earlier contact.
  const sessionFields = requestSessionFields();

  return {
    /** `acceleratorContactBefore` and `documentCookieLength` at send time. */
    sessionFields(): CartFlowFields {
      return sessionFields;
    },
    /** Call as soon as the accelerator answered, whatever the status. */
    received() {
      markAcceleratorContact();
    },
    overlapping(): string {
      const others = [...inFlightRequests]
        .filter(([otherKey]) => otherKey !== key)
        .map(([, otherKind]) => otherKind)
        .sort((first, second) => first.localeCompare(second));

      return others.length > 0 ? others.join(",") : "none";
    },
    end() {
      inFlightRequests.delete(key);
    },
  };
}

// --- Flow --------------------------------------------------------------------

async function resolveSecrets(step: BufferedStep): Promise<CartFlowFields> {
  const fields: CartFlowFields = { ...step.fields };

  if (!step.secrets) {
    return fields;
  }

  await Promise.all(
    Object.entries(step.secrets).map(async ([name, secret]) => {
      fields[name] = await fingerprint(secret);
    })
  );

  return fields;
}

interface WrittenStep {
  event: string;
  fields: CartFlowFields;
}

/** Keys of the step that failed, lifted to the top level of the RUM context. */
const RUM_DIAGNOSTIC_KEYS = [
  "httpStatus",
  "statusClass",
  "correlationId",
  "finalPath",
  "contentType",
  "bodyKind",
  "bodyLength",
  "durationMs",
  "errorName",
  "errorKind",
  "noResponse",
  "netProtocol",
  "netStatus",
  "netTotalMs",
  "netRedirectMs",
  "netDnsMs",
  "netConnectMs",
  "netTlsMs",
  "netWaitMs",
  "netDownloadMs",
  "netConnectionReused",
  "netTransferBytes",
  "netBodyBytes",
] as const;

/** Keys that every step repeats; the per-step context leaves them out. */
const REPEATED_STEP_KEYS = new Set(["event", "visibility", "pageAgeMs"]);

/**
 * One RUM error per failed attempt, readable on its own in the RUM Explorer:
 * the categorizing fields and the failing step's facts on the top level, every
 * step under `steps.<name>`.
 */
function buildRumContext(
  flowFields: CartFlowFields,
  result: CartFlowResult,
  written: WrittenStep[]
): Record<string, unknown> {
  const resultEvent = `${EVENT_PREFIX}.result`;
  const resultStep = written.find((step) => step.event === resultEvent);
  // The failing step is the last request, not the result that summarizes it.
  const failingStep = [...written]
    .reverse()
    .find(
      (step) =>
        step.event !== resultEvent &&
        (step.fields.httpStatus !== undefined ||
          step.fields.errorName !== undefined)
    )?.fields;
  const steps = Object.fromEntries(
    written.map((step) => [
      stepKey(step.event),
      definedFields(
        Object.fromEntries(
          Object.entries(step.fields).filter(
            ([key]) => !(key in flowFields || REPEATED_STEP_KEYS.has(key))
          )
        )
      ),
    ])
  );

  return {
    ...definedFields(flowFields),
    phase: result.phase,
    errorCategory: result.reason,
    outcome: result.outcome,
    postAttempted: result.postAttempted,
    outcomeKnown: result.outcomeKnown,
    summary: resultStep
      ? readableMessage(resultEvent, resultStep.fields)
      : undefined,
    totalDurationMs: resultStep?.fields.totalDurationMs,
    acceleratorContactBefore: resultStep?.fields.acceleratorContactBefore,
    documentCookieLength: resultStep?.fields.documentCookieLength,
    errorMsg: resultStep?.fields.errorMsg,
    ...definedFields(
      Object.fromEntries(
        RUM_DIAGNOSTIC_KEYS.map((key) => [key, failingStep?.[key]])
      )
    ),
    steps,
  };
}

/**
 * Error Tracking needs a stack trace and groups by type and message, so the
 * message is constant per phase and category; the variable facts live in the
 * context. A caught exception is kept as `cause`.
 */
function buildRumError(result: CartFlowResult): Error {
  const rumError = new Error(
    `${MESSAGE_PREFIX}${result.phase}/${result.reason}`,
    result.error === undefined ? undefined : { cause: result.error }
  );
  rumError.name = "AddToCartError";

  return rumError;
}

function reportRumError(
  flowFields: CartFlowFields,
  result: CartFlowResult,
  written: WrittenStep[]
) {
  try {
    browserLogger.rum.addError(
      buildRumError(result),
      buildRumContext(flowFields, result, written)
    );
  } catch {
    // Telemetry must never affect the storefront.
  }
}

const NOOP_FLOW: CartFlow = {
  id: "none",
  step() {},
  finish() {},
};

/**
 * Starts the diagnostics of one add-to-cart click. Returns a no-op flow if
 * telemetry cannot be set up, so callers never need a fallback of their own.
 */
export function startAddToCartFlow(context: CartFlowContext): CartFlow {
  try {
    return createFlow(context);
  } catch {
    return NOOP_FLOW;
  }
}

function createFlow(context: CartFlowContext): CartFlow {
  clickCount += 1;
  const id = createId();
  const startedAt = now();
  const clickSeq = clickCount;
  const ddInitialized = isDatadogInitialized();
  const flowFields: CartFlowFields = {
    operation: OPERATION,
    attemptId: id,
    clickSeq,
    locale: `${context.country}/${context.language}`,
    productCode: context.productCode,
    quantity: context.quantity,
    stockStatus: context.stockStatus,
    ddInitialized,
  };
  const buffer: BufferedStep[] = [];
  let state: "open" | "flushed" = "open";

  activeFlow = { id, startedAt };

  function toBufferedStep(
    event: string,
    fields: CartFlowFields,
    options?: StepOptions
  ): BufferedStep {
    return {
      event: `${EVENT_PREFIX}.${event}`,
      level: options?.level ?? "info",
      fields,
      secrets: options?.secrets,
      elapsedMs: Math.round(now() - startedAt),
      pageAgeMs: Math.round(now()),
    };
  }

  async function writeSteps(steps: BufferedStep[]): Promise<WrittenStep[]> {
    const resolved = await Promise.all(steps.map(resolveSecrets));

    return steps.map((step, index) => {
      const fields: CartFlowFields = {
        ...flowFields,
        ...resolved[index],
        visibility: visibilityState(),
        elapsedMs: step.elapsedMs,
        pageAgeMs: step.pageAgeMs,
      };
      writeLog(step.level, step.event, fields);

      return { event: step.event, fields };
    });
  }

  function flush(steps: BufferedStep[], result?: CartFlowResult) {
    const pending = writeSteps(steps)
      .then((written) => {
        if (result) {
          reportRumError(flowFields, result, written);
        }
      })
      .catch(() => {
        // Telemetry must never affect the storefront.
      })
      .finally(() => {
        pendingWrites.delete(pending);
      });

    pendingWrites.add(pending);
  }

  const flow: CartFlow = {
    id,
    step(event, fields, options) {
      try {
        const step = toBufferedStep(event, fields ?? {}, options);

        if (state === "flushed") {
          flush([step]);
          return;
        }

        buffer.push(step);
      } catch {
        // Telemetry must never affect the storefront.
      }
    },
    finish(result) {
      try {
        if (state !== "open") {
          return;
        }

        if (activeFlow?.id === id) {
          activeFlow = null;
        }

        lastAttempt = {
          outcome: result.outcome,
          errorCategory: result.reason,
          at: now(),
        };

        let level: LogLevel = "info";
        if (result.outcome === "error") {
          level = "error";
        } else if (result.outcome === "unknown") {
          level = "warn";
        }

        // The status of the last request goes on the result as well, so the
        // outcome can be searched and read without opening the other events.
        const lastResponse = [...buffer]
          .reverse()
          .find(
            (step) =>
              step.fields.httpStatus !== undefined ||
              step.fields.errorName !== undefined
          )?.fields;

        // What the browser knew about its session when the last request (or,
        // without one, the click) was sent.
        const sentWith = [...buffer]
          .reverse()
          .find(
            (step) => step.fields.acceleratorContactBefore !== undefined
          )?.fields;

        buffer.push(
          toBufferedStep(
            "result",
            {
              outcome: result.outcome,
              errorCategory: result.reason,
              phase: result.phase,
              postAttempted: result.postAttempted,
              outcomeKnown: result.outcomeKnown,
              totalDurationMs: Math.round(now() - startedAt),
              httpStatus: lastResponse?.httpStatus,
              statusClass: lastResponse?.statusClass,
              correlationId: lastResponse?.correlationId,
              acceleratorContactBefore: sentWith?.acceleratorContactBefore,
              documentCookieLength: sentWith?.documentCookieLength,
              errorName: result.errorName,
              errorMsg: sanitizeText(result.errorMessage) || undefined,
            },
            { level }
          )
        );

        const succeeded = result.outcome === "success";

        // A confirmed add is routine: every event of it is plain info.
        if (succeeded) {
          for (const step of buffer) {
            step.level = "info";
          }
        }

        state = "flushed";
        flush(buffer.splice(0), succeeded ? undefined : result);
      } catch {
        // Telemetry must never affect the storefront.
      }
    },
  };

  // The attempt before this one (read before this attempt can overwrite it).
  const previous = lastAttempt;

  flow.step("click", {
    ...requestSessionFields(),
    previousOutcome: previous?.outcome ?? "none",
    previousErrorCategory: previous?.errorCategory,
    previousAttemptAgoMs: previous
      ? Math.round(now() - previous.at)
      : undefined,
    lastCartCountFailureAgoMs: lastCartCountFailure
      ? Math.round(now() - lastCartCountFailure.at)
      : undefined,
    lastCartCountFailureStatus: lastCartCountFailure?.status,
    lastCartCountSuccessAgoMs: sinceLastCartCountSuccess(),
  });

  return flow;
}

// --- Header mini-cart --------------------------------------------------------

/**
 * Reports a failed mini-cart refresh. Failures are problems by definition, so
 * they are written immediately; the number per page session is capped so an
 * outage cannot flood Datadog through focus/pageshow refreshes.
 */
export function reportCartCountFailure(fields: CartFlowFields): void {
  try {
    const status =
      typeof fields.httpStatus === "number" ? fields.httpStatus : undefined;
    lastCartCountFailure = { at: now(), status };
    cartCountFailuresInARow += 1;

    if (cartCountFailureLogs >= MAX_CART_COUNT_FAILURE_LOGS) {
      return;
    }

    cartCountFailureLogs += 1;
    writeLog("warn", CART_COUNT_EVENT, {
      operation: "cart_count",
      ...fields,
      // Is this a one-off or an outage, and did it ever work on this page?
      failuresInARow: cartCountFailuresInARow,
      successesThisPage: cartCountSuccesses,
      lastSuccessAgoMs: sinceLastCartCountSuccess(),
      overlapsAttemptId: activeFlow?.id,
      attemptElapsedMs: activeFlow
        ? Math.round(now() - activeFlow.startedAt)
        : undefined,
      ddInitialized: isDatadogInitialized(),
      visibility: visibilityState(),
      pageAgeMs: Math.round(now()),
    });
  } catch {
    // Telemetry must never affect the storefront.
  }
}

function sinceLastCartCountSuccess(): number | undefined {
  return lastCartCountSuccessAt === null
    ? undefined
    : Math.round(now() - lastCartCountSuccessAt);
}

/** A mini-cart refresh worked: this ends a run of failures. */
export function recordCartCountSuccess(): void {
  cartCountFailuresInARow = 0;
  cartCountSuccesses += 1;
  lastCartCountSuccessAt = now();
}

/**
 * What made the next mini-cart refresh start (`focus`, `pageshow`,
 * `cartUpdated`, `otherTab`). The refresh function takes it over right away,
 * so the hook's call signature stays as it is.
 */
export function noteCartCountTrigger(name: string): void {
  pendingCartCountTrigger = { name, at: now() };
}

/** The trigger noted just before this refresh started, if there is one. */
export function takeCartCountTrigger(): string | undefined {
  const pending = pendingCartCountTrigger;
  pendingCartCountTrigger = null;

  return pending && now() - pending.at < CART_COUNT_TRIGGER_MAX_AGE_MS
    ? pending.name
    : undefined;
}

/**
 * Test hook: resolves once every attempt that was handed over for writing has
 * been written (fingerprints are hashed asynchronously).
 */
export async function cartFlowTelemetrySettled(): Promise<void> {
  while (pendingWrites.size > 0) {
    await Promise.all([...pendingWrites]);
  }
}

/** Test hook: reset module state between tests. */
export function resetCartFlowTelemetry(): void {
  clickCount = 0;
  activeFlow = null;
  requestCounter = 0;
  cartCountFailureLogs = 0;
  lastCartCountFailure = null;
  acceleratorResponses = 0;
  cartCountFailuresInARow = 0;
  cartCountSuccesses = 0;
  lastCartCountSuccessAt = null;
  lastAttempt = null;
  pendingCartCountTrigger = null;
  inFlightRequests.clear();

  try {
    sessionStorage.removeItem(CONTACT_STORAGE_KEY);
  } catch {
    // Storage may be unavailable.
  }
}
